import React from 'react';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { areLinkedTasksEnabled } from '~src/linkedTasks/utils/LinkedTaskHelpers';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { explorerSubspace } from '~src/spacesPlatform/components/spaceComponents/SubspaceHelpers';
import { Subspace, TabbedSpace } from '~src/spacesPlatform/components/spaceComponents/TabbedSpace';
import { ISpaceInfoTypeItemCount } from '~src/spacesPlatform/state/ContentCountsState';
import { ServerTasksSubspaceName, SiteTasksSubspaceName, SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { Messages } from '../../../codegen/Localize';
import { ExtractTaskDefinition } from '../../../contentDefinitions/ExtractTaskDefinition';
import { AccelerationTaskDefinition } from '../../../dataAcceleration/contentDefinitions/AccelerationTaskDefinition';
import { DataAlertDefinition } from '../../../dda/contentDefinitions/DataAlertDefinition';
import { RunFlowTaskDefinition } from '../../../flow/contentDefinitions/RunFlowTaskDefinition';
import { SubscriptionDefinition } from '../../../subscription/contentDefinitions/SubscriptionDefinition';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { ServerUrls } from '../../ServerUrls';

export interface TasksSpaceStateProps {
  readonly spaceInfo: SpaceInfo;
  readonly contentCountsSiteTasks: ISpaceInfoTypeItemCount<'sitetasks'>;
  readonly contentCountsServerTasks: ISpaceInfoTypeItemCount<'servertasks'>;
  readonly contentCountsUpToDate: boolean;
  readonly isDataAlertsEnabled: boolean;
  readonly isFlowOnSiteEnabled: boolean;
  readonly isFlowOnServerEnabled: boolean;
  readonly schedulingFlowsEnabled: boolean;
  readonly isUserAdmin: boolean;
}

export interface TasksSpaceDispatchProps {
  readonly fetchUpdatedContentCounts: () => void;
}

export interface TasksSpaceOwnProps {
  readonly urls: SpacesSiteUrls;
  readonly inServerArea?: boolean;
}

export type TasksSpaceProps = TasksSpaceStateProps & TasksSpaceOwnProps & TasksSpaceDispatchProps;

export class TasksSpace extends React.PureComponent<TasksSpaceProps> {

  private memoizedGetTasksSubspaces = defaultMemoize(getTasksSubspaces);

  public componentDidUpdate(): void {
    this.fetchUpdatedCounts();
  }

  public componentDidMount(): void {
    this.fetchUpdatedCounts();
  }

  private static siteUrlBySubspace: Record<SiteTasksSubspaceName, (urls: SpacesSiteUrls) => string> = {
    dataAcceleration: (urls) => urls.tasksDataAccelerationUrl(),
    extractRefreshes: (urls) => urls.tasksExtractRefreshesUrl(),
    runFlowTasks: (urls) => urls.tasksFlowsUrl(),
    subscriptions: (urls) =>  urls.tasksSubscriptionsUrl(),
    alerts: (urls) =>  urls.tasksDataAlertsUrl(),
    linkedTasks: (urls) => urls.tasksLinkedTasksUrl(),
  };

  public getSiteTasksUrl = (subspaceName?: SiteTasksSubspaceName) => {
    let url = this.props.urls.tasksUrl();
    if (subspaceName) {
      const urlFn = TasksSpace.siteUrlBySubspace[subspaceName];
      url = urlFn && urlFn(this.props.urls) || url;
    }
    return url;
  }

  private static serverUrlBySubspace: Record<ServerTasksSubspaceName, () => string> = {
    dataAcceleration: () => ServerUrls.getInstance().tasksDataAccelerationUrl(),
    extractRefreshes: () => ServerUrls.getInstance().tasksExtractRefreshesUrl(),
    runFlowTasks: () => ServerUrls.getInstance().tasksFlowsUrl(),
    subscriptions: () =>  ServerUrls.getInstance().tasksSubscriptionsUrl(),
    linkedTasks: () => ServerUrls.getInstance().tasksLinkedTasksUrl(),
  };

  public getServerTasksUrl = (subspaceName?: ServerTasksSubspaceName) => {
    let url = ServerUrls.getInstance().tasksUrl();
    if (subspaceName) {
      const urlFn = TasksSpace.serverUrlBySubspace[subspaceName];
      url = urlFn && urlFn() || url;
    }
    return url;
  }

  public render() {
    const subspaces = this.memoizedGetTasksSubspaces(
      this.props.contentCountsSiteTasks,
      this.props.contentCountsServerTasks,
      this.props.contentCountsUpToDate,
      this.props.isDataAlertsEnabled,
      this.props.isFlowOnSiteEnabled,
      this.props.isFlowOnServerEnabled,
      this.props.schedulingFlowsEnabled,
      this.props.inServerArea,
      this.props.isUserAdmin
    );

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.spaceInfo.subspaceName}
        urlForSubspace={this.props.inServerArea ? this.getServerTasksUrl : this.getSiteTasksUrl}
        mastheadElement={undefined}  // For tasks, there isn't a masthead, but we are keeping the padding from masthead parent to stay consistent for the other admin spaces
        testIdPrefix={this.props.inServerArea ? 'servertasks' : 'sitetasks'}
        spaceItemName={undefined}  // For tasks, we don't need a separate browser title, just use the subspace
      />
    );
  }

  private fetchUpdatedCounts = (): void => {
    this.props.fetchUpdatedContentCounts();
  }
}

function getContentCount(contentCountsSiteTasks: ISpaceInfoTypeItemCount<'sitetasks'>,
contentCountsServerTasks: ISpaceInfoTypeItemCount<'servertasks'>, inServerArea: boolean, contentName: string): number {
  if (inServerArea) {
    return contentCountsServerTasks && contentCountsServerTasks[contentName as ServerTasksSubspaceName];
  } else {
    return contentCountsSiteTasks && contentCountsSiteTasks[contentName as SiteTasksSubspaceName];
  }
}

function getTasksSubspaces(
  contentCountsSiteTasks: ISpaceInfoTypeItemCount<'sitetasks'>,
  contentCountsServerTasks: ISpaceInfoTypeItemCount<'servertasks'>,
  contentCountsUpToDate: boolean,
  isDataAlertsEnabled: boolean,
  isFlowOnSiteEnabled: boolean,
  isFlowOnServerEnabled: boolean,
  schedulingFlowsEnabled: boolean,
  inServerArea: boolean,
  isUserAdmin: boolean
): ReadonlyArray<Subspace> {
  return _.compact<_.List<Subspace | false>>([
    explorerSubspace({
      name: 'extractRefreshes',
      tabLabel: Messages.extractTasks(),
      supportedDisplayModes: ExtractTaskDefinition.supportedDisplayModes,
      contentCount: getContentCount(contentCountsSiteTasks,
        contentCountsServerTasks, inServerArea, 'extractRefreshes'),
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      supportFilterPanel: false,
      emptyStateProps: {
        primaryMessage: Messages.tasksEmptyRefreshes()
      }
    }),
    ((isFlowOnSiteEnabled && schedulingFlowsEnabled && !inServerArea) ||
    (isFlowOnServerEnabled && schedulingFlowsEnabled && inServerArea)) && explorerSubspace({
      name: 'runFlowTasks',
      tabLabel: Messages.flows(),
      supportedDisplayModes: RunFlowTaskDefinition.supportedDisplayModes,
      contentCount: getContentCount(contentCountsSiteTasks,
        contentCountsServerTasks, inServerArea, 'runFlowTasks'),
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      supportFilterPanel: false,
      emptyStateProps: {
        primaryMessage: Messages.tasksEmptyFlows()
      }
    }),
    explorerSubspace({
      name: 'subscriptions',
      tabLabel: Messages.subscriptions(),
      supportedDisplayModes: SubscriptionDefinition.supportedDisplayModes,
      contentCount: getContentCount(contentCountsSiteTasks,
        contentCountsServerTasks, inServerArea, 'subscriptions'),
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      supportFilterPanel: false,
      emptyStateProps: {
        primaryMessage: Messages.tasksEmptySubscriptions()
      }
    }),
    FeatureFlags.alertOnThresholdConditionEnabled && isDataAlertsEnabled && !inServerArea
     && explorerSubspace({
      name: 'alerts',
      tabLabel: Messages.dataAlerts(),
      supportedDisplayModes: DataAlertDefinition.supportedDisplayModes,
      contentCount: contentCountsSiteTasks && contentCountsSiteTasks['alerts'],
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      supportFilterPanel: false,
      emptyStateProps: {
        primaryMessage: Messages.tasksEmptyAlerts()
      }
    }),
    FeatureFlags.accelerationSiteAdminUIEnabled && explorerSubspace({
      name: 'dataAcceleration',
      tabLabel: Messages.acceleratedViews(),
      supportedDisplayModes: AccelerationTaskDefinition.supportedDisplayModes,
      contentCount: getContentCount(contentCountsSiteTasks,
        contentCountsServerTasks, inServerArea, 'dataAcceleration'),
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      supportFilterPanel: isUserAdmin,
      emptyStateProps: {
        primaryMessage: Messages.tasksEmptyWorkbookAcceleration()
      }
    }),
    areLinkedTasksEnabled() && explorerSubspace({
      name: 'linkedTasks',
      tabLabel: Messages.linkedTasks(),
      supportedDisplayModes: RunFlowTaskDefinition.supportedDisplayModes,
      contentCount: getContentCount(contentCountsSiteTasks,
        contentCountsServerTasks, inServerArea, 'linkedTasks'),
      shouldRenderContentCount: true,
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      supportFilterPanel: false,
      emptyStateProps: {
        primaryMessage: Messages.tasksEmptyLinkedTasks()
      }
    })
  ]);
}
